import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { cn } from "../../../../lib/utils";
import useRoles from "../../../../hooks/useRoles";
import {
  capitalizeFirstLetter,
  getAge,
  getLabelForScheduleType,
  getTotalCapsulesLabel,
  pluralizeUnit,
  prescriberName,
} from "../../../../shared/helpers";
import {
  fieldLabel,
  fieldValue,
  pafSectionLabelClass,
  VIEW_PAF_ROOT_CLASS,
} from "../constants/viewPafConstants";
import { typeOfPAF } from "../../../../constants/typeOfPAF";
import { isInpatientPharmacyEditablePafStatus } from "../utils/viewPafFormLogic";

function extractIndication(form) {
  if (typeof form?.indication === "string") {
    if (form?.indication?.includes("Other")) {
      return `Other (${form?.indication_other})`;
    }
    if (form?.indication?.includes("Clinical trial")) {
      return `Clinical Trial (${form?.clinical_trial})`;
    }
    return form?.indication;
  }
  if (typeof form?.indication === "object" && form?.indication) {
    if (form.indication?.label?.includes("Other")) {
      return `Other (${form?.indication_other})`;
    }
    if (form.indication?.label?.includes("Clinical trial")) {
      return `Clinical Trial (${form?.clinical_trial})`;
    }
    return form.indication?.label;
  }
  return null;
}

function getPatientCategory(form) {
  if (form?.patient?.patient_type) return form.patient.patient_type;
  if (form?.patient_type) return form.patient_type;
  if (form?.type) {
    switch (form.type) {
      case "male":
        return "Male";
      case "womanOfNCP":
        return "Woman of Non-Childbearing Potential";
      case "womanOfCP":
        return "Woman of Childbearing Potential";
      default:
        break;
    }
  }
  const seg = window.location.pathname.split("/")[3];
  if (seg === "womanOfNCP") return "Woman of Non-Childbearing Potential";
  if (seg === "male") return "Male";
  return "Woman of Childbearing Potential";
}

function capsuleLine(form, element, unitLabel) {
  if (element?.new_capsules === "Other (please state)") {
    return `${element.new_capsules_other} ${pluralizeUnit(capitalizeFirstLetter(unitLabel || "capsule"))}`;
  }
  if (element?.new_capsules) {
    return `${element.new_capsules} ${pluralizeUnit(capitalizeFirstLetter(unitLabel || "capsule"))}`;
  }
  if (element?.capsules !== "Incomplete" && element?.capsules != null) {
    return `${element.capsules} ${pluralizeUnit(capitalizeFirstLetter(unitLabel || "capsule"))}`;
  }
  return "Incomplete";
}

function nCyclesLine(form) {
  if (form?.number_cycles?.label != null && form?.number_cycles?.id != null) {
    return form.number_cycles.label;
  }
  if (form?.new_capsule_cycles) {
    return form.new_capsule_cycles.label;
  }
  return form?.n_cycles || "Incomplete";
}

function nWeeksLine(form) {
  if (
    form?.new_capsule_cycles &&
    form?.new_capsule_cycles?.label !== "Other (Please State)"
  ) {
    return form?.new_capsule_cycles?.label;
  }
  if (form?.other) return `${form.other} Weeks`;
  if (form?.number_weeks?.id) return `${form.number_weeks.id} Weeks`;
  if (form?.n_weeks_other) return `${form.n_weeks_other} Weeks`;
  return form?.n_weeks || "Incomplete";
}

function weeksPerCycleLine(form) {
  if (form?.new_weeks_per_cycle && form?.new_weeks_per_cycle?.id !== 0) {
    return form?.new_weeks_per_cycle?.label;
  }
  if (form?.new_weeks_per_cycle?.id === 0) {
    return `${form?.new_weeks_per_cycle_other} weeks`;
  }
  if (form?.weeks_per_cycle?.id === 0) {
    return `${form?.other} weeks`;
  }
  if (form?.weeks_per_cycle?.id) {
    return `${form.weeks_per_cycle.id} weeks`;
  }
  return "Incomplete";
}

function totalSuppliedPrimary(form, scheduleDisplayType, unitLabel) {
  const u = capitalizeFirstLetter(unitLabel || "capsule");
  if (scheduleDisplayType === "weeks_per_cycle") {
    return form?.total_weeks_supply > 0
      ? `${form.total_weeks_supply} weeks`
      : "Incomplete";
  }
  if (form?.new_totalCapsules > 0) {
    return `${form.new_totalCapsules} ${pluralizeUnit(u)}`;
  }
  if (form?.total_capsules > 0) {
    return `${form.total_capsules} ${pluralizeUnit(u)}`;
  }
  return "Incomplete";
}

const incompleteClass = (text) =>
  text === "Incomplete" ? "red-label font-size-16" : "font-size-16";

function strengthValueContent(element) {
  if (element?.strength_value != null && element.strength_value !== "") {
    return `${element.strength_value} mg`;
  }
  return null;
}

/** Patient Initiation wizard: strength label from `capsules[].capsuleStrength` (e.g. "2.5 mg"). */
function wizardStrengthText(capsule) {
  const cs = capsule?.capsuleStrength;
  const raw =
    cs?.value ??
    cs?.label ??
    (typeof cs === "string" ? cs : null);
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim();
  }
  return null;
}

/** Quantity per row for wizard `capsules[]` (uses `label` / `other`, not ViewPAF `new_capsules`). */
function wizardCapsuleQuantityLine(capsule, unitLabel) {
  const u = pluralizeUnit(capitalizeFirstLetter(unitLabel || "capsule"));
  if (capsule?.label === undefined || capsule?.label === null) {
    return "Incomplete";
  }
  if (capsule.label === 0 && capsule.other != null && capsule.other !== false) {
    return `${capsule.other} ${u}`;
  }
  if (capsule.label === 0) {
    return "Incomplete";
  }
  return `${capsule.label} ${u}`;
}

/**
 * ViewPAF / API: `form.strengths` is an array of `{ strength_value, new_capsules, … }`.
 * Patient Initiation: `form.strengths` may be `{}`; use `form.capsules[]` with `capsuleStrength`.
 */
function getStrengthRows(form) {
  if (Array.isArray(form?.strengths) && form.strengths.length > 0) {
    return { mode: "viewpaf", rows: form.strengths };
  }
  if (Array.isArray(form?.capsules) && form.capsules.length > 0) {
    return { mode: "wizard", rows: form.capsules };
  }
  return { mode: "empty", rows: [] };
}

/**
 * PAF summary for Patient Initiation step 6 (after Digital PAF), aligned with ViewPAF
 * confirmation blocks: prescription-field risks + `form.prescription_confirmation_risks`.
 */
const PostCreatePAFReview = ({
  form,
  patientDetails,
  prescriptionField,
  storageData,
  /** Same keys as ViewPAF `checkedItems`: `risk-checkbox-${id}` */
  riskCheckboxValues = {},
  /** Same keys as `checkedItemsPrescriber`: `checkbox-prescriber-${id}` */
  prescriberCheckboxValues = {},
  showErrors,
  /** Pharmacy: approval decision known — locks patient-confirmation rows (ViewPAF parity) */
  isFormApproved = null,
  /** Pharmacy on Submitted/Overdue/Expiring: first risk block, `(event) => …` */
  onSubmittedRiskCheckboxChange,
  /** Pharmacy on Submitted/Overdue/Expiring: second block, `(event, index) => …` */
  onSubmittedPatientConfirmationChange,
}) => {
  const { isPharmacy } = useRoles();
  const [initialConfirmation, setInitialConfirmation] = useState([]);

  useEffect(() => {
    if (form && Array.isArray(form?.prescription_confirmation_risks)) {
      setInitialConfirmation([...form.prescription_confirmation_risks]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ViewPAF pattern: run when API array appears
  }, [!!form?.prescription_confirmation_risks]);

  const pharmacyCanEditSubmitted =
    isInpatientPharmacyEditablePafStatus(form) && isPharmacy;

  const scheduleDisplayType =
    prescriptionField?.schedule_display_type || form?.schedule_display_type;
  const displayWeeks = form?.schedule_display_type === "weeks";
  const unitLabel = form?.unit_label || "capsule";

  const handlePatientConfirmationChange = (event, index) => {
    if (pharmacyCanEditSubmitted) {
      onSubmittedPatientConfirmationChange?.(event, index);
    }
  };

  const summaryPatient = useMemo(() => {
    const nested =
      form?.patient && typeof form.patient === "object" ? form.patient : {};
    const hasFlatWizardFields =
      form &&
      (form.patient_dob != null ||
        form.patient_initials != null ||
        form.id != null);
    const fromFlat = hasFlatWizardFields
      ? {
        id: form.id ?? nested.id,
        patient_initials: form.patient_initials ?? nested.patient_initials,
        dob: form.patient_dob ?? form.dob ?? nested.dob,
      }
      : {};
    return {
      ...nested,
      ...fromFlat,
      ...(patientDetails || {}),
    };
  }, [patientDetails, form]);

  const dobRaw = summaryPatient?.dob ?? form?.patient_dob;
  const isDobMissing = dobRaw == null || dobRaw === "";
  const dobAge = dobRaw && !isDobMissing ? getAge(dobRaw) : null;
  const isDobUnder18 =
    dobAge != null && !Number.isNaN(dobAge) && dobAge < 18;
  const isDobError = isDobMissing || isDobUnder18;

  const formatDob = () => {
    if (isDobMissing) return null;
    return moment(dobRaw).format("DD / MM / YYYY");
  };

  const handedHospitalName =
    storageData?.hospital?.value + ", " + storageData?.hospital?.address;

  const getPharmacyFromLocalStorage = () =>
    storageData?.hospital?.value + ", " + storageData?.hospital?.address;

  const treatingHospitalTop =
    form?.hospital_name_and_address ||
    form?.patient?.hospital ||
    patientDetails?.hospital ||
    patientDetails?.treatingHospital ||
    summaryPatient?.hospital ||
    handedHospitalName;

  const hospitalAddressBlock =
    form?.hospital_name_and_address ||
    form?.patient?.hospital ||
    treatingHospitalTop;

  const pharmacyAddress =
    form?.pharmacy_address ||
    form?.patient?.pharmacy ||
    getPharmacyFromLocalStorage();

  const dateOfPaf = form?.pfa_issued
    ? moment(form.pfa_issued).format("DD / MM / YYYY")
    : moment().format("DD / MM / YYYY");

  const isWomanOfCP = form?.type === typeOfPAF.WOMANOFCP;
  const pregnancyTestRaw =
    form?.pregnancy_test ??
    form?.patient?.pregnancy_test ??
    summaryPatient?.pregnancy_test;
  const lastNegativePregnancyTestDisplay =
    pregnancyTestRaw != null && pregnancyTestRaw !== ""
      ? moment(pregnancyTestRaw).format("DD / MM / YYYY")
      : null;

  const moleculeLabel =
    form?.molecule?.label || form?.drug || form?.product || "—";

  const patientIdDisplay = summaryPatient?.id ?? "—";

  const genderDisplay =
    patientDetails?.gender ??
    (form?.type === "male"
      ? "Male"
      : form?.type === "womanOfNCP" || form?.type === "womanOfCP"
        ? "Female"
        : "—");

  const capSecondHeader = () => {
    if (scheduleDisplayType === "weeks_per_cycle") {
      return "Supply weeks per cycle:*";
    }
    return prescriptionField?.schedule_display_type === "weeks" || displayWeeks
      ? `Number of ${pluralizeUnit(capitalizeFirstLetter(unitLabel))} per 4 week supply:*`
      : `${pluralizeUnit(capitalizeFirstLetter(unitLabel))} per Cycle*:`;
  };

  const { mode: strengthMode, rows: strengthRows } = useMemo(
    () => getStrengthRows(form),
    [form]
  );
  const hasStrengthRows = strengthRows.length > 0;

  const strengthDisplay =
    !hasStrengthRows ? null : (
      <div className="space-y-1">
        {strengthRows.map((element, index) => {
          if (strengthMode === "viewpaf") {
            const line = strengthValueContent(element);
            return (
              <div key={index}>
                {line != null ? (
                  line
                ) : (
                  <span className="red-label font-size-16">Incomplete</span>
                )}
              </div>
            );
          }
          const w = wizardStrengthText(element);
          return (
            <div key={index}>
              {w != null ? (
                w
              ) : (
                <span className="red-label font-size-16">Incomplete</span>
              )}
            </div>
          );
        })}
      </div>
    );

  const capsulesPerSupplyDisplay =
    scheduleDisplayType === "weeks_per_cycle" || !hasStrengthRows
      ? null
      : (
        <div className="space-y-1">
          {strengthRows.map((element, index) => {
            const line =
              strengthMode === "viewpaf"
                ? capsuleLine(form, element, unitLabel)
                : wizardCapsuleQuantityLine(element, unitLabel);
            return (
              <div key={index}>
                <span className={incompleteClass(line)}>{line}</span>
              </div>
            );
          })}
        </div>
      );

  const secondColumnValue = () => {
    if (scheduleDisplayType === "weeks_per_cycle") {
      const line = weeksPerCycleLine(form);
      return (
        <span className={incompleteClass(line)}>{line}</span>
      );
    }
    if (!hasStrengthRows) {
      return <span className="red-label font-size-16">Incomplete</span>;
    }
    return capsulesPerSupplyDisplay;
  };

  const thirdColumnValue = () => {
    if (scheduleDisplayType === "weeks_per_cycle") {
      const line = nCyclesLine(form);
      return <span className={incompleteClass(line)}>{line}</span>;
    }
    if (displayWeeks || form?.number_weeks?.label?.includes?.("Weeks")) {
      const line = nWeeksLine(form);
      return <span className={incompleteClass(line)}>{line}</span>;
    }
    const line = nCyclesLine(form);
    return <span className={incompleteClass(line)}>{line}</span>;
  };

  const totalPrimaryText = totalSuppliedPrimary(
    form,
    scheduleDisplayType,
    unitLabel
  );

  const isWeeksPerCycleSchedule = scheduleDisplayType === "weeks_per_cycle";

  /** Per-strength quantities (same rows as Strength); separate column for `weeks_per_cycle` only (col 2 is supply weeks/cycle). */
  const capsuleQuantityColumnDisplay = !hasStrengthRows ? (
    <span className="red-label font-size-16">Incomplete</span>
  ) : (
    <div className="space-y-1">
      {strengthRows.map((element, index) => {
        const line =
          strengthMode === "viewpaf"
            ? capsuleLine(form, element, unitLabel)
            : wizardCapsuleQuantityLine(element, unitLabel);
        return (
          <div key={index}>
            {line === "Incomplete" ? (
              <span className="font-semibold text-[#3c3c3c]">-</span>
            ) : (
              <span className={incompleteClass(line)}>{line}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={VIEW_PAF_ROOT_CLASS}>
      <div className="flex w-full flex-col overflow-hidden rounded-lg">
        <div className="px-5 pb-8 pt-5">
          <div
            className={cn(
              "grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
            )}
          >
            <div>
              <div className={fieldLabel} data-testid="view-paf-patient-id-label">Patient ID</div>
              <div className={fieldValue} data-testid="view-paf-patient-id-value">{patientIdDisplay}</div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-patient-initials-label">Patient Initials</div>
              <div className={fieldValue} data-testid="view-paf-patient-initials-value">
                {summaryPatient?.patient_initials ?? "—"}
              </div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-gender-label">Gender</div>
              <div className={fieldValue} data-testid="view-paf-gender-value">{genderDisplay}</div>
            </div>
            <div>
              <div
                className={cn(
                  fieldLabel,
                  isDobError && "text-[#e41818]"
                )}
                data-testid="view-paf-date-of-birth-label"
              >
                Date of Birth
              </div>
              <div className={fieldValue} data-testid="view-paf-date-of-birth-value">
                {isDobMissing ? (
                  <span className="red-label font-size-16">Incomplete</span>
                ) : (
                  <span
                    className={cn(
                      isDobError && "red-label font-size-16"
                    )}
                  >
                    {formatDob()}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-treating-hospital-label">Treating Hospital</div>
              <div className={fieldValue} data-testid="view-paf-treating-hospital-value">{treatingHospitalTop ?? "—"}</div>
            </div>
          </div>
        </div>

        <div className="min-w-0 flex-1 px-5 py-5 pb-6">
          <label className={cn(pafSectionLabelClass, "mb-3")} data-testid="view-paf-patient-details-label">Patient Details</label>
          <div className="mb-6 grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <div className={fieldLabel} data-testid="view-paf-patient-category-label">Patient Category</div>
              <div className={fieldValue} data-testid="view-paf-patient-category-value">{getPatientCategory(form) ?? "—"}</div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-indication-label">Indication</div>
              <div className={fieldValue} data-testid="view-paf-indication-value">
                {extractIndication(form) || (
                  <span className="red-label font-size-16">Incomplete</span>
                )}
              </div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-molecule-label">Molecule</div>
              <div className={fieldValue} data-testid="view-paf-molecule-value">{moleculeLabel}</div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-date-of-paf-label">Date of PAF</div>
              <div className={fieldValue} data-testid="view-paf-date-of-paf-value">{dateOfPaf}</div>
            </div>
            {isWomanOfCP && (
              <div>
                <div className={fieldLabel} data-testid="view-paf-last-negative-pregnancy-test-label">Last Negative Pregnancy Test</div>
                <div className={fieldValue} data-testid="view-paf-last-negative-pregnancy-test-value">
                  {lastNegativePregnancyTestDisplay ? (
                    lastNegativePregnancyTestDisplay
                  ) : (
                    <span className="red-label font-size-16">Incomplete</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2">
            <div>
              {prescriptionField?.prescription_confirmation_risks?.map(
                (item) => {
                  const key = `risk-checkbox-${item.id}`;
                  const checked = !!riskCheckboxValues[key];
                  const labelRed =
                    riskCheckboxValues[key] === false ||
                    (showErrors && !checked);

                  const canEditBlock1 =
                    pharmacyCanEditSubmitted && onSubmittedRiskCheckboxChange;

                  return (
                    <div key={item.id} className="mt-2 mb-2">
                      <label
                        className={cn(
                          "custom-checkbox font-size-16 dove-gray",
                          labelRed && "red-label"
                        )}
                        htmlFor={`risk-checkbox-input-${item.id}`}
                        data-testid={`view-paf-risk-confirmation-${item.id}-label`}
                      >
                        {item.confirmation_text}
                        <input
                          type="checkbox"
                          id={`risk-checkbox-input-${item.id}`}
                          name={key}
                          checked={checked}
                          onChange={
                            canEditBlock1
                              ? (event) =>
                                onSubmittedRiskCheckboxChange(event)
                              : undefined
                          }
                          disabled={!canEditBlock1}
                          data-testid={`view-paf-risk-confirmation-${item.id}-checkbox`}
                        />
                        <span className="checkmark mt-0" />
                      </label>
                    </div>
                  );
                }
              )}
            </div>
            <div>
              {Array.isArray(form?.prescription_confirmation_risks) &&
                form.prescription_confirmation_risks.map((item, index) => {
                  const canEditBlock2 =
                    pharmacyCanEditSubmitted &&
                    onSubmittedPatientConfirmationChange;

                  return (
                    <div key={index} className="mt-2 mb-2">
                      <label
                        className={cn(
                          "custom-checkbox font-size-16 dove-gray",
                          item.confirmation_checked_by_pharmacist === true &&
                          "green-haze",
                          item.confirmation_checked_by_pharmacist !== true &&
                          item.confirmation_risk_patient === false &&
                          "red-label"
                        )}
                        htmlFor={`confirmation-${index}`}
                        data-testid={`view-paf-confirmation-${index}-label`}
                      >
                        {item.confirmation_text}{" "}
                        <input
                          type="checkbox"
                          id={`confirmation-${index}`}
                          name={`confirmation-${index}`}
                          checked={!!item.confirmation_risk_patient}
                          onChange={(event) =>
                            handlePatientConfirmationChange(event, index)
                          }
                          disabled={
                            !canEditBlock2 ||
                            (isPharmacy && isFormApproved !== null
                              ? true
                              : !!initialConfirmation[index]
                                ?.confirmation_risk_patient)
                          }
                          tabIndex={!canEditBlock2 ? -1 : undefined}
                          data-testid={`view-paf-confirmation-${index}-checkbox`}
                        />
                        <span className="checkmark mt-0" />
                      </label>
                    </div>
                  );
                })}
            </div>

          </div>





          <hr className="my-6 border-0 border-t border-[#dcdcdc]" />

          <div className="mb-6 w-full max-w-full overflow-x-auto overscroll-x-contain">
            <div
              className={cn(
                "grid w-full min-w-[820px] gap-x-3 gap-y-3.5 xl:min-w-0",
                isWeeksPerCycleSchedule ? "grid-cols-5" : "grid-cols-4"
              )}
            >
              <div className="min-w-0">
                <div className={fieldLabel} data-testid="view-paf-capsule-strength-label">
                  {`${capitalizeFirstLetter(unitLabel)} Strength`}
                </div>
                <div className={fieldValue} data-testid="view-paf-capsule-strength-value">
                  {!hasStrengthRows ? (
                    <span className="red-label font-size-16">Incomplete</span>
                  ) : (
                    strengthDisplay
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <div
                  className={fieldLabel}
                  data-testid={
                    isWeeksPerCycleSchedule
                      ? "view-paf-supply-weeks-per-cycle-label"
                      : "view-paf-capsules-per-cycle-label"
                  }
                >
                  {capSecondHeader()}
                </div>
                <div
                  className={fieldValue}
                  data-testid={
                    isWeeksPerCycleSchedule
                      ? "view-paf-supply-weeks-per-cycle-value"
                      : "view-paf-capsules-per-cycle-value"
                  }
                >
                  {secondColumnValue()}
                </div>
              </div>
              <div className="min-w-0">
                <div className={fieldLabel} data-testid="view-paf-number-of-cycles-label">
                  {getLabelForScheduleType(scheduleDisplayType)}
                </div>
                <div className={fieldValue} data-testid="view-paf-number-of-cycles-value">{thirdColumnValue()}</div>
              </div>
              {isWeeksPerCycleSchedule && (
                <div className="min-w-0">
                  <div className={fieldLabel} data-testid="view-paf-capsule-quantity-label">
                    {`${capitalizeFirstLetter(unitLabel)} Quantity`}
                  </div>
                  <div className={fieldValue} data-testid="view-paf-capsule-quantity-value">{capsuleQuantityColumnDisplay}</div>
                </div>
              )}
              <div className="min-w-0">
                <div className={fieldLabel} data-testid="view-paf-total-supply-label">
                  {getTotalCapsulesLabel(scheduleDisplayType, form?.unit_label)}
                </div>
                <div className={fieldValue} data-testid="view-paf-total-supply-value">
                  <span
                    className={
                      totalPrimaryText === "Incomplete"
                        ? "red-label font-size-16"
                        : "font-size-16"
                    }
                  >
                    {totalPrimaryText}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-6 border-0 border-t border-[#dcdcdc]" />

          <div className="mt-2">
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 py-3.5 md:grid-cols-2">
              <div>
                <div className={fieldLabel} data-testid="view-paf-prescriber-label">Prescriber</div>
                <div className={fieldValue} data-testid="view-paf-prescriber-value">{prescriberName()}</div>
              </div>
              <div>
                <div className={fieldLabel} data-testid="view-paf-hospital-address-label">Hospital Address</div>
                <div className={fieldValue} data-testid="view-paf-hospital-address-value">{hospitalAddressBlock ?? "—"}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 py-3.5 md:grid-cols-2">
              <div>
                <div className={fieldLabel} data-testid="view-paf-approved-by-label">Approved By</div>
                <div className={fieldValue} data-testid="view-paf-approved-by-value">Awaiting approval</div>
              </div>
              <div>
                <div className={fieldLabel} data-testid="view-paf-pharmacy-address-label">Pharmacy Address</div>
                <div className={fieldValue} data-testid="view-paf-pharmacy-address-value">{pharmacyAddress ?? "—"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PostCreatePAFReview.propTypes = {
  form: PropTypes.object.isRequired,
  patientDetails: PropTypes.object,
  prescriptionField: PropTypes.object,
  storageData: PropTypes.object,
  riskCheckboxValues: PropTypes.object,
  prescriberCheckboxValues: PropTypes.object,
  showErrors: PropTypes.bool,
  isFormApproved: PropTypes.bool,
  onSubmittedRiskCheckboxChange: PropTypes.func,
  onSubmittedPatientConfirmationChange: PropTypes.func,
};

export default PostCreatePAFReview;
